// src/ui/CallChatHistory.jsx — what was said DURING a call, after it ended.
//
// 🟢 THE SAME THING THE PHONE'S CallChatHistoryScreen SHOWS, from the same place:
//    chats/{chatId}/call_threads/{callSessionId}/messages, ordered by time. It is
//    a plain, unencrypted subcollection on both clients, so there is nothing to
//    decrypt and nothing to keep in step — one listener, one list.
//
// 🟥 IT IS NOT PART OF THE CONVERSATION. Messages sent during a call live in
//    their own thread precisely so they do not land in the chat afterwards, which
//    is why they are invisible until somebody opens the call that produced them.
//    That makes the call row the only door to them, and this the room behind it.
//
// 🟢 IT IS A BAR, NOT A DIALOG. It used to open as a centred modal over the
//    conversation, which put the call's chat and the chat it belongs to in the
//    same place at different depths — you could read one or the other. A side
//    panel puts them next to each other, and it drags to whatever width the
//    reader wants, like every other bar in the app. The width is remembered,
//    because a preference re-entered on every call is not a preference.
//
// 🟥 BELOW ~900px THERE IS NOTHING TO TRADE AGAINST. Splitting a phone-width
//    window leaves two unusable columns, so under that width the panel stops
//    being a split and slides OVER the conversation with a backdrop — and the
//    drag handle goes away with it, rather than staying as a control that
//    cannot help.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { X } from './icons';
import Avatar from './Avatar';
import { fullTimeLabel } from './format';
import { tsMs } from '../services/chats';

const WIDTH_KEY = 'allo_call_thread_w';
const MIN_W = 300;
const MAX_W = 720;
/* the conversation still needs room to be a conversation */
const KEEP_FOR_CHAT = 380;
const NARROW = 900;

function storedWidth() {
  try {
    const n = Number(localStorage.getItem(WIDTH_KEY));
    return n >= MIN_W && n <= MAX_W ? n : 400;
  } catch { return 400; }
}

/** True while the window is too narrow to split — re-evaluated on resize. */
function useNarrow() {
  const [narrow, setNarrow] = useState(
    () => (typeof window !== 'undefined' ? window.innerWidth < NARROW : false),
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${NARROW - 1}px)`);
    const on = () => setNarrow(mq.matches);
    on();
    /* addListener is the Safari-15-and-older spelling; both are harmless */
    if (mq.addEventListener) mq.addEventListener('change', on);
    else mq.addListener(on);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', on);
      else mq.removeListener(on);
    };
  }, []);
  return narrow;
}

export default function CallChatHistory({ chatId, callSessionId, myUid, onClose }) {
  const [msgs, setMsgs] = useState(null);   // null = still loading
  const [err, setErr] = useState('');
  const [width, setWidth] = useState(storedWidth);
  const [dragging, setDragging] = useState(false);
  const narrow = useNarrow();
  const endRef = useRef(null);

  /* ── the thread itself ─────────────────────────────────────────────────── */
  useEffect(() => {
    if (!chatId || !callSessionId) { setMsgs([]); return undefined; }
    setMsgs(null);
    setErr('');
    /* 🟢 A LISTENER RATHER THAN ONE READ. The status row is written the moment
       the HOST leaves, but everyone else may still be in the call and still
       typing — a one-shot read would show whatever happened to be there when
       the panel opened and then quietly go stale. */
    const unsub = onSnapshot(
      query(
        collection(db, 'chats', chatId, 'call_threads', callSessionId, 'messages'),
        orderBy('timestamp', 'asc'),
      ),
      (snap) => setMsgs(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => { setErr('That call’s messages could not be loaded.'); setMsgs([]); },
    );
    return () => { try { unsub(); } catch { /* already torn down */ } };
  }, [chatId, callSessionId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [msgs]);

  /* 🟢 THE SAME SQUEEZE THE CALL DOCK USES (calls.css, `.call-open`): a class on
     the app root plus a width variable, so the conversation genuinely re-wraps
     into the room that is left instead of sliding underneath a floating panel.
     The two widths add, so an open 1:1 call dock and this bar coexist. Done by
     touching the root node rather than lifting state through four components,
     which is the same trade the call dock made and for the same reason. */
  useEffect(() => {
    const root = document.querySelector('.allo-app');
    if (!root || narrow) return undefined;
    root.classList.add('thread-open');
    return () => {
      root.classList.remove('thread-open');
      root.style.removeProperty('--cch-w');
    };
  }, [narrow]);

  useEffect(() => {
    const root = document.querySelector('.allo-app');
    if (!root) return;
    if (narrow) root.style.removeProperty('--cch-w');
    else root.style.setProperty('--cch-w', `${width}px`);
  }, [width, narrow]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  /* ── the drag handle ───────────────────────────────────────────────────── */
  const onGrip = useCallback((e) => {
    if (narrow) return;
    e.preventDefault();
    setDragging(true);
    const move = (ev) => {
      /* measured from the right edge, because that is the edge it is pinned to */
      const raw = window.innerWidth - ev.clientX;
      const cap = Math.min(MAX_W, Math.max(MIN_W, window.innerWidth - KEEP_FOR_CHAT));
      setWidth(Math.max(MIN_W, Math.min(cap, raw)));
    };
    const up = () => {
      setDragging(false);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      setWidth((w) => { try { localStorage.setItem(WIDTH_KEY, String(w)); } catch { /* private mode */ } return w; });
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }, [narrow]);

  /* double-click resets to the default, the way a split pane usually does */
  const onGripDouble = useCallback(() => {
    setWidth(400);
    try { localStorage.setItem(WIDTH_KEY, '400'); } catch { /* private mode */ }
  }, []);

  const count = msgs ? msgs.length : 0;

  const panel = (
    <aside
      className={`cch ${narrow ? 'over' : 'split'} ${dragging ? 'dragging' : ''}`}
      style={narrow ? undefined : { width: `${width}px` }}
      aria-label="Messages during this call"
    >
      {!narrow && (
        <div
          className="cch-grip"
          onPointerDown={onGrip}
          onDoubleClick={onGripDouble}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize this panel"
          title="Drag to resize · double-click to reset"
        />
      )}
      <div className="cch-head">
        <div className="cch-title">
          <b>Messages during this call</b>
          {count > 0 && <i>{count} message{count === 1 ? '' : 's'}</i>}
        </div>
        <button className="icon-btn" onClick={onClose} aria-label="Close"><X /></button>
      </div>
      <div className="cch-body">
        {msgs === null && <div className="cch-empty">Loading…</div>}
        {msgs !== null && msgs.length === 0 && (
          <div className="cch-empty">{err || 'Nothing was said in the chat during this call.'}</div>
        )}
        {(msgs || []).map((m) => (
          <div key={m.id} className={`cch-msg ${m.senderId === myUid ? 'mine' : ''}`}>
            {m.senderId !== myUid && (
              <Avatar profile={{ fullName: m.senderName, profileImage: m.senderImage }} size="sm" />
            )}
            <div>
              {m.senderId !== myUid && <b>{m.senderName || 'Allo user'}</b>}
              <span>{m.text}</span>
              <i>{fullTimeLabel(tsMs(m.timestamp))}</i>
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
    </aside>
  );

  /* the backdrop exists only when the panel covers something */
  if (!narrow) return panel;
  return (
    <div className="cch-veil" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      {panel}
    </div>
  );
}
